import React from "react";

import { Category, Option } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiLink2 } from "react-icons/fi";

import { BookedByDate } from "../../../../../../../../../components/Orders/components/BookedByDate";
import useOrderOptions from "../../../../../../../../../components/Orders/getOrderOptions";
import BadgePOStatus from "../../../../../../../../../components/Orders/StatusBadge";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { Customer } from "../../../../../../../../../models/customer";
import { Port } from "../../../../../../../../../models/port";
import { PurchaseOrder } from "../../../../../../../../../models/purchase.order";
import { Supplier } from "../../../../../../../../../models/supplier";
import PortService from "../../../../../../../../../services/port.service";
import PurchaseOrderService from "../../../../../../../../../services/purchase.order.service";
import SupplierService from "../../../../../../../../../services/supplier.service";
import { ColumnWithKey } from "../../../../../../../../../types";
import { OrderDetailsFields } from "../../../../../../../../../util/enums";
import { formatDateWithoutTimeZone } from "../../../../../../../../../util/Format";
import { useActionReportMaintenance } from "../../../hooks/useAction";
import { useReportState } from "../../../hooks/useReportState";
import { convertValueToSearch } from "../../../utils";
import { BaseReportGrid } from "../../BaseReportGrid";
import useStyles from "./styles";

export const OrdersReport = () => {
  const { getAllPurchaseOrders } = useActionReportMaintenance();
  const { isSupplierPortal, currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { orderTypes, orderStatusOptions, incotermOptions, modeOptions } = useOrderOptions();
  const {
    filters,
    setFilters,
    tempFilters,
    setTempFilters,
    allCurrentFilters,
    dateFilters,
    setDateFilters,
    type,
  } = useReportState(false);

  const renderReferenceFromIntegration = (reference: string, order: PurchaseOrder) => {
    if (!order.fromIntegration) return reference;

    return (
      <div className={classes.row}>
        <span>{reference}</span>
        <FiLink2 color="#38CFC4" size={15} />
      </div>
    );
  };

  const columns: ColumnWithKey[] = [
    {
      name: "reference",
      title: `${translate("order")} #`,
      searchIdentifier: "purchase_order_id",
      formatComponent: renderReferenceFromIntegration,
      hideShowColumn: true,
    },
    { name: "type", title: translate("type") },
    {
      name: "mode",
      title: translate("mode"),
      formatComponent: (value: string) =>
        value ? translate(value.toLowerCase()) : "",
    },
    isSupplierPortal
      ? {
          name: "consignee",
          title: translate("consignee"),
          key: "supplierName",
          formatText: (consignee?: Customer) => consignee?.name || "",
          searchIdentifier: "consignee.name",
        }
      : {
          name: "supplierName",
          title: translate("supplier"),
          key: "supplierName",
          searchIdentifier: "supplier_name",
        },
    { name: "productTotalQuantity", title: translate("unitQty") },
    { name: "incoterm", title: translate("incoterm") },
    {
      name: "origin",
      title: translate("originPort"),
      searchIdentifier: "origin",
    },
    {
      name: "eta",
      title: translate("shipByDate"),
      key: "shipByDate",
      formatText: (value?: string) => formatDateWithoutTimeZone(value),
      hideSearch: true,
    },
    {
      name: "eta",
      title: translate("bookByDate"),
      key: "bookByDate",
      formatComponent: (value: string) => <BookedByDate value={value} />,
      hideSearch: true,
    },
    {
      name: "destination",
      title: translate("destinationPort"),
      searchIdentifier: "destination",
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => (status ? <BadgePOStatus value={status} /> : null),
      hideSearch: true,
    },
    {
      name: "createdAt",
      title: translate("createdAt"),
      formatText: (value) => formatDateWithoutTimeZone(value),
    },
  ];

  const filterCategories: Category[] = [
    {
      name: `${translate("order")} #`,
      column: "id",
      onSearch: (value: string) =>
        PurchaseOrderService.getAllNetwork({
          group: ["purchase_order.id"],
          companyId: currentUser?.companyId,
          include: JSON.stringify([]),
          attributes: ["id", "reference"],
          searchByOr: JSON.stringify(convertValueToSearch(value, "purchase_order_id")),
        }),
      getOptionLabel: (option: PurchaseOrder) => option?.reference || "",
    },
    {
      name: translate(OrderDetailsFields.TYPE),
      column: OrderDetailsFields.TYPE,
      options: orderTypes,
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
    {
      name: translate("mode"),
      column: "mode",
      options: modeOptions,
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
    {
      name: translate("incoterm"),
      column: "incoterm",
      options: incotermOptions,
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
    {
      name: translate("supplier"),
      column: "supplierId",
      onSearch: (value: string) =>
        SupplierService.getAll({
          companyId: currentUser?.companyId,
          searchByOr: JSON.stringify(convertValueToSearch(value, "name")),
          attributes: ["id", "name", "associatedCompanyId"],
          isCompany: true,
        }),
      getOptionLabel: (option: Supplier) => option?.name || "",
      optionColumn: "associatedCompanyId",
    },
    {
      name: translate("originPort"),
      column: "originPortId",
      onSearch: (value: string) =>
        PortService.getAll({
          searchByOr: JSON.stringify(convertValueToSearch(value, "site_code")),
        }),
      getOptionLabel: (option: Port) => option?.siteCode || "",
    },
    {
      name: translate("destinationPort"),
      column: "destinationPortId",
      onSearch: (value: string) =>
        PortService.getAll({
          searchByOr: JSON.stringify(convertValueToSearch(value, "site_code")),
        }),
      getOptionLabel: (option: Port) => option?.siteCode || "",
    },
    {
      name: translate("status"),
      column: "status",
      options: orderStatusOptions,
      getOptionLabel: (option: Option) => option?.label || "",
      optionColumn: "value",
    },
  ];

  const getResourcesPerReport = () => {
    switch (type) {
      default:
        return {
          keyExtractor: (data: any) => data.id,
          query: getAllPurchaseOrders,
          columns,
        };
    }
  };

  return (
    <BaseReportGrid
      keyExtractor={getResourcesPerReport().keyExtractor}
      getAllData={getResourcesPerReport().query}
      columns={getResourcesPerReport().columns}
      dateFilters={dateFilters}
      setDateFilters={setDateFilters}
      filterCategories={filterCategories}
      filters={filters}
      setFilters={setFilters}
      tempFilters={tempFilters}
      setTempFilters={setTempFilters}
      allCurrentFilters={allCurrentFilters}
      isDataLocal={false}
    />
  );
};
